import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { of, switchMap } from 'rxjs';
import { AuthService } from '../../../core/auth/auth.service';
import { CompanyService } from '../services/company.service';

function checkCompanyAccess(
    router: Router,
    state: any,
    status: { id: number | null; enabled: boolean },
    email: string,
    companyService: CompanyService
) {
    // Vérifier si c'est la page d'édition de la société
    if (state.url.startsWith('/companies/edit/')) {
        // Extraire l'ID de l'URL
        const urlParts = state.url.split('/');
        const companyIdFromUrl = urlParts[urlParts.length - 1];
        
        // Si la société n'est pas activée, autoriser seulement l'accès à sa propre page d'édition
        if (!status.enabled) {
            if (status.id && companyIdFromUrl === status.id.toString()) {
                return of(true); // Autoriser l'accès à sa propre page d'édition
            } else {
                // Rediriger vers la page d'édition de sa propre société
                if (status.id) {
                    router.navigate([`/companies/edit/${status.id}`]);
                } else {
                    // Si pas d'ID, essayer de charger le statut
                    return companyService.loadCompanyStatus(email).pipe(
                        switchMap(loadedStatus => {
                            if (loadedStatus.id) {
                                router.navigate([`/companies/edit/${loadedStatus.id}`]);
                            }
                            return of(false);
                        })
                    );
                }
                return of(false);
            }
        }
        // Si la société est activée, autoriser l'accès à toutes les pages d'édition
        return of(true);
    }

    // Pour les autres pages, vérifier si la société est activée
    if (!status.enabled) {
        // Rediriger vers la page d'édition de la société pour l'activer
        if (status.id) {
            router.navigate([`/companies/edit/${status.id}`]);
        } else {
            // Si pas d'ID, essayer de charger le statut
            return companyService.loadCompanyStatus(email).pipe(
                switchMap(loadedStatus => {
                    if (loadedStatus.id) {
                        router.navigate([`/companies/edit/${loadedStatus.id}`]);
                    }
                    return of(false);
                })
            );
        }
        return of(false);
    }
    return of(true);
}

export const CompanyEnabledGuard: CanActivateFn | CanActivateChildFn = (route, state) => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const companyService = inject(CompanyService);

    // SOLUTION RADICALE : Autoriser l'accès à CRA pour tous sans vérification
    if (state.url.startsWith('/cra')) {
        return of(true);
    }

    // Toujours autoriser l'accès aux routes companies (y compris /companies/edit/:id)
    // Ces routes sont gérées par leurs propres guards (CompanyOnlyGuard)
    if (state.url.startsWith('/companies')) {
        return of(true);
    }

    const email = localStorage.getItem('email');
    if (!email) {
        router.navigate(['/sign-in']);
        return of(false);
    }

    // Candidat, ressource ou collaborateur : pas de vérification (l'activation concerne le compte propriétaire)
    if (authService.isCandidate() || authService.isResource() || authService.isManager()) {
        return of(true);
    }

    // Si l'utilisateur est un super admin, autoriser l'accès
    if (authService.isSuperAdmin()) {
        return of(true);
    }

    // Pour les companies, vérifier le statut d'activation
    if (authService.isCompany()) {
        // Obtenir le statut actuel de la société
        const currentStatus = companyService.getCurrentCompanyStatus();
        
        // Si le statut n'est pas encore chargé, le charger
        if (!currentStatus.id && email) {
            return companyService.loadCompanyStatus(email).pipe(
                switchMap(loadedStatus => {
                    return checkCompanyAccess(router, state, loadedStatus, email, companyService);
                })
            );
        }
        
        return checkCompanyAccess(router, state, currentStatus, email, companyService);
    }

    // Pour les autres utilisateurs, permettre l'accès
    return of(true);
};
